/**
 * Organizations — admin list view.
 *
 * Backend gives us status as a server-side filter; name/email search
 * and pagination are client-side because the API doesn't yet expose
 * those parameters. When it does, swap the in-memory `useMemo` for
 * a query-string round-trip — call sites already use `useDebounce`.
 *
 * Member counts aren't on AdminOrganization; rendered as "—" until an
 * aggregate lands. Federated origin is shown as a small "via partner"
 * tag below the name (we don't have the partner display name here).
 */
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { listAgents } from '@optum/api-client/endpoints/agents';
import { listOrganizations } from '@optum/api-client/endpoints/organizations';
import { listTasks } from '@optum/api-client/endpoints/tasks';
import type { AdminOrganization, OrganizationStatus } from '@optum/api-client/types';

import { useAuth } from '../../auth/AuthContext.js';
import { useRecordPanel } from '../../components/shell/record.js';
import { useOpenRecordPage } from '../../components/shell/record-page.js';
import { ChipFilter, ListToolbar } from '../../ui/ListToolbar.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { shortId } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { useWindowStart } from '../../lib/use-time-window.js';
import { useDebounce } from '../../lib/use-debounce.js';
import { DataTable } from '../../ui/DataTable.js';
import { ListShape } from '../../ui/shapes.js';
import { Avatar, IconButton, Pagination } from '../../ui/finixt.js';
import { orgStatusLabel } from './org-status.js';
import { Button } from '../../ui/Button.js';

type StatusKey = 'all' | OrganizationStatus;

const STATUS_FILTERS: ReadonlyArray<{ key: StatusKey; label: string }> = [
  { key: 'all', label: 'All statuses' },
  { key: 'active', label: 'Active' },
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'inactive', label: 'Disabled' },
  { key: 'rejected', label: 'Rejected' },
];

const PAGE_SIZE = 20;

export function OrganizationsPage() {
  const navigate = useNavigate();
  const { isPlatformAdmin } = useAuth();
  const [status, setStatus] = useState<StatusKey>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const debounced = useDebounce(search, 200);

  const filters = status === 'all' ? {} : { status };
  const query = useQuery({
    queryKey: qk.orgs(filters),
    queryFn: () => listOrganizations(filters),
  });

  const filtered = useMemo(
    () => filterByText(query.data ?? [], debounced),
    [query.data, debounced],
  );
  const pageStart = page * PAGE_SIZE;
  const pageRows = filtered.slice(pageStart, pageStart + PAGE_SIZE);

  // Tasks come from one grouped query. Agents cannot: the list endpoint
  // requires an organization_id, so it is one request per visible row —
  // bounded by the page size, and only for rows actually on screen.
  const since = useWindowStart(24 * 60 * 60 * 1000);
  const tasks = useQuery({
    queryKey: qk.tasks({ created_after: since }),
    queryFn: () => listTasks({ created_after: since, limit: 500 }),
  });
  const taskCounts = useMemo(() => {
    const out: Record<string, number> = {};
    for (const t of tasks.data ?? []) {
      out[t.organization_id] = (out[t.organization_id] ?? 0) + 1;
    }
    return out;
  }, [tasks.data]);

  const agentQueries = useQueries({
    queries: pageRows.map((org) => ({
      queryKey: qk.agents(org.id),
      queryFn: () => listAgents({ organization_id: org.id }),
      staleTime: 60_000,
    })),
  });
  const agentCounts = useMemo(() => {
    const out: Record<string, number> = {};
    pageRows.forEach((org, i) => {
      const data = agentQueries[i]?.data;
      if (data) out[org.id] = data.length;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageRows.map((o) => o.id).join(','), agentQueries.map((q) => q.data?.length).join(',')]);

  return (
    <ListShape
      title="Organizations"
      subtitle={`${query.data?.length ?? 0} tenants · open one to read its record, or enter its workspace from the row.`}
      action={
        isPlatformAdmin ? (
          <Button variant="primary" icon="add" onClick={() => navigate('/organizations/new')}>
            New organization
          </Button>
        ) : undefined
      }
    >
      <ListToolbar
        search={search}
        onSearch={(v) => {
          setSearch(v);
          setPage(0);
        }}
        placeholder="Search organizations…"
        filters={
          <ChipFilter
            options={STATUS_FILTERS}
            value={status}
            onChange={(next) => {
              setStatus(next);
              setPage(0);
            }}
          />
        }
      />
      {query.isPending && (
        <Padded>
          <LoadingBlock />
        </Padded>
      )}
      {query.isError && (
        <Padded>
          <ErrorBlock
            message={query.error instanceof Error ? query.error.message : 'Failed to load'}
          />
        </Padded>
      )}
      {query.data && filtered.length === 0 && (
        <Padded>
          <EmptyBlock>No organizations match.</EmptyBlock>
        </Padded>
      )}
      {query.data && pageRows.length > 0 && (
        <OrganizationsTable
          rows={pageRows}
          total={filtered.length}
          page={page}
          onPage={setPage}
          count={query.data ? `${filtered.length} of ${query.data.length} organizations` : '—'}
          agentCounts={agentCounts}
          taskCounts={taskCounts}
        />
      )}
    </ListShape>
  );
}

function Padded({ children }: { children: ReactNode }) {
  return <div style={{ padding: 22 }}>{children}</div>;
}

/** Two letters from the name, matching the design's avatar tiles. */
function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0]![0]! + words[1]![0]!).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

function filterByText(rows: AdminOrganization[], text: string): AdminOrganization[] {
  const q = text.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter(
    (r) => r.name.toLowerCase().includes(q) || (r.contact_email ?? '').toLowerCase().includes(q),
  );
}

function OrganizationsTable({
  rows,
  total,
  page,
  onPage,
  count,
  agentCounts,
  taskCounts,
}: {
  rows: AdminOrganization[];
  total: number;
  page: number;
  onPage: (p: number) => void;
  count: string;
  agentCounts: Record<string, number>;
  taskCounts: Record<string, number>;
}) {
  const navigate = useNavigate();
  const panel = useRecordPanel();
  const openPage = useOpenRecordPage();
  return (
    <DataTable
      count={count}
      hint="click a row for the record · → enters the workspace"
      pager={
        <Pagination
          total={total}
          page={page + 1}
          pageSize={PAGE_SIZE}
          onChange={(next) => onPage(next - 1)}
        />
      }
      rows={rows}
      selectedKey={panel.current?.kind === 'organization' ? panel.current.id : undefined}
      onRowClick={(org) => panel.open({ kind: 'organization', id: org.id })}
      onRowOpenPage={(org) => openPage({ kind: 'organization', id: org.id })}
      columns={[
        {
          key: 'name',
          label: 'Organization',
          strong: true,
          render: (org) => (
            <span style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <Avatar initials={initialsOf(org.name)} size={32} />
              <span
                style={{
                  fontSize: 'var(--text-body-md)',
                  fontWeight: 'var(--weight-medium)',
                  color: 'var(--text-strong)',
                }}
              >
                {org.name}
              </span>
            </span>
          ),
        },
        {
          key: 'id',
          label: 'Identifier',
          mono: true,
          render: (org) => (
            <>
              {shortId(org.id)}
              {org.federated_partner_id && (
                <span style={{ marginLeft: 7, color: 'var(--warn)' }}>· via partner</span>
              )}
            </>
          ),
        },
        {
          key: 'status',
          label: 'Status',
          render: (org) => <StatusBadge status={org.status} label={orgStatusLabel(org.status)} />,
        },
        {
          key: 'agents',
          label: 'Agents',
          align: 'right',
          mono: true,
          render: (org) => agentCounts[org.id] ?? '—',
        },
        {
          key: 'tasks',
          label: 'Tasks 24h',
          align: 'right',
          mono: true,
          render: (org) => (taskCounts[org.id] ?? 0).toLocaleString(),
        },
        {
          key: 'enter',
          label: '',
          align: 'right',
          stopRowClick: true,
          render: (org) => (
            <IconButton
              icon="arrow_forward"
              size={30}
              title={`Enter ${org.name}'s workspace`}
              aria-label={`Enter ${org.name}'s workspace`}
              onClick={() => navigate(`/orgs/${org.id}/overview`)}
              tone="outline"
            />
          ),
        },
      ]}
    />
  );
}
